import Check from '@mui/icons-material/Check';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import { Box, Button, Card, Typography, useTheme } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import { orange } from '~/styles/tokens/primitives.colors';
import { borderWidth, radius } from '~/styles/tokens/shape';

export type PaymentConfirmFlowVariant = 'payment' | 'promotion' | 'guarantee';

/**
 * Fundo da tarja de aviso adaptado ao modo de cor:
 * - light: pastel `orange[100]` (#FFF3E6), como no design;
 * - dark: laranja translúcido sobre a superfície escura, mantendo o texto (`text.primary`) legível.
 */
export function getAlertSurfaceBackground(theme: Theme): string {
  return theme.palette.mode === 'dark' ? alpha(theme.palette.warning.main, 0.15) : orange[100];
}

type PaymentConfirmSuccessCardProps = {
  variant: PaymentConfirmFlowVariant;
  onFillContract: () => void;
  onFinishLater: () => void;
};

function headlineCopy(variant: PaymentConfirmFlowVariant) {
  if (variant === 'promotion') return 'Promoção ativada!';
  if (variant === 'guarantee') return 'Garantia ativada!';
  return 'Pagamento aprovado!';
}

function alertCopy(variant: PaymentConfirmFlowVariant) {
  if (variant === 'guarantee') {
    return 'Conclua o preenchimento dos dados do contrato para a sua garantia começar a valer.';
  }
  return 'Somente podemos concluir a contratação após o preenchimento da próxima etapa.';
}

function primaryButtonLabel(variant: PaymentConfirmFlowVariant) {
  if (variant === 'guarantee') return 'Preencher dados agora';
  return 'Preencher dados do contrato';
}

export default function PaymentConfirmSuccessCard({
  variant,
  onFillContract,
  onFinishLater
}: PaymentConfirmSuccessCardProps) {
  const theme = useTheme();
  const tertiaryMain = theme.customColors.tertiaryMain;

  return (
    <Card
      data-app-id="journey-payment-confirm-success-card"
      elevation={0}
      sx={{
        borderRadius: `${radius.large}px`,
        boxShadow: `0px 4px 4px ${theme.customColors.elevation}, 0px 1px 1.5px rgba(0,0,0,0.3)`,
        maxWidth: 694,
        mx: 'auto',
        p: '24px',
        width: '100%'
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <Box sx={{ alignItems: 'center', display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
          <Box
            sx={{
              alignItems: 'center',
              bgcolor: 'success.main',
              borderRadius: '100px',
              color: theme.palette.common.white,
              display: 'flex',
              flexShrink: 0,
              height: 64,
              justifyContent: 'center',
              width: 64
            }}
          >
            <Check aria-hidden sx={{ fontSize: 32 }} />
          </Box>
          <Typography
            component="h1"
            data-app-id="journey-payment-confirm-headline"
            sx={{ fontWeight: 600 }}
            variant="h5"
          >
            {headlineCopy(variant)}
          </Typography>
        </Box>

        <Box
          sx={(theme) => ({
            alignItems: 'flex-start',
            bgcolor: getAlertSurfaceBackground(theme),
            border: `${borderWidth.small}px solid`,
            borderColor: 'warning.dark',
            borderRadius: `${radius.medium}px`,
            display: 'flex',
            gap: 1,
            p: 2
          })}
        >
          <InfoOutlined aria-hidden sx={{ color: 'text.primary', flexShrink: 0, mt: 0.25 }} />
          <Typography data-app-id="journey-payment-confirm-alert" variant="subtitle1">
            {alertCopy(variant)}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <Button
            data-app-id="journey-payment-confirm-fill-contract"
            fullWidth
            onClick={onFillContract}
            size="large"
            sx={{ borderRadius: `${radius.large}px` }}
            variant="contained"
          >
            {primaryButtonLabel(variant)}
          </Button>
          <Button
            data-app-id="journey-payment-confirm-finish-later"
            fullWidth
            onClick={onFinishLater}
            size="large"
            sx={{
              '&:hover': { borderColor: tertiaryMain, borderWidth: borderWidth.medium },
              borderColor: tertiaryMain,
              borderRadius: `${radius.large}px`,
              borderWidth: borderWidth.medium,
              color: tertiaryMain
            }}
            variant="outlined"
          >
            Finalizar mais tarde
          </Button>
        </Box>
      </Box>
    </Card>
  );
}
